import React from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   Library.jsx — Soft Tech Talks (Brief landing)
   - Pure React + index.css classes (no external UI deps)
   - High-level overview only; deep docs live on /docs/* pages
   - Update: added softauto (alphabetical), subtle vintage-mag vibes
------------------------------------------------------------------- */

function Badge({ children }) {
  return <span className="stt-badge stt-badge-vintage">{children}</span>;
}

function Orn({ align = "center" }) {
  return (
    <div className={`stt-orn stt-orn-${align}`} aria-hidden>
      <span className="stt-orn-left" />
      <span className="stt-orn-glyph">✦</span>
      <span className="stt-orn-right" />
    </div>
  );
}

function Card({ title, tone = "neutral", kicker, children, footer }) {
  return (
    <section className={`stt-card stt-card-${tone} stt-card-vintage`}>
      {kicker ? <p className="stt-kicker">{kicker}</p> : null}
      {title ? <h3 className="stt-card-title stt-title-vintage">{title}</h3> : null}
      <Orn />
      <div className="stt-card-body">{children}</div>
      {footer ? <div className="stt-card-footer">{footer}</div> : null}
    </section>
  );
}

export default function Library() {
  return (
    <main className="stt-wrap stt-wrap-vintage">
      {/* Hero */}
      <header className="stt-hero stt-hero-vintage">
        <div className="stt-pill">Soft Tech Talks • Libraries</div>
        <h1 className="stt-h1 stt-h1-vintage">Build fast. Learn deeply. Share simply.</h1>
        <p className="stt-lead stt-lead-vintage">
          Tiny tools that remove setup friction so students and teams can focus on the thinking.
        </p>
        <Orn />
      </header>

      {/* Quick principles (brief) */}
      <section className="stt-section stt-grid-3">
        <Card tone="neutral" kicker="Principle I">
          <p className="stt-p stt-dropcap">🧠 <strong>Math-first:</strong> understand what the model does first.</p>
        </Card>
        <Card tone="neutral" kicker="Principle II">
          <p className="stt-p stt-dropcap">🛠️ <strong>No boilerplate:</strong> clean APIs; copy-ready examples.</p>
        </Card>
        <Card tone="neutral" kicker="Principle III">
          <p className="stt-p stt-dropcap">🚀 <strong>Fast starts:</strong> go from idea to demo in minutes.</p>
        </Card>
      </section>

      {/* Library briefs (alphabetical) */}
      <section className="stt-section stt-grid-2">
        {/* softapi */}
        <Card
          title="softapi — scaffold backends"
          tone="rose"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softapi" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softapi" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            Create a production-lean FastAPI project with <strong>one command</strong>. Includes JWT auth,
            DB wiring (SQLite/Postgres), and a sane file layout.
          </p>
          <div className="stt-kv">
            <Badge>FastAPI</Badge><Badge>JWT</Badge><Badge>Postgres/SQLite</Badge><Badge>Typer CLI</Badge>
          </div>
        </Card>

        {/* softauth */}
        <Card
          title="softauth — JWT auth, plug & play"
          tone="teal"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softauth" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softauth" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            Zero-setup JWT authentication for FastAPI and Flask. Plug-in decorators, token-lifecycle
            management, and role-based guards out of the box.
          </p>
          <div className="stt-kv">
            <Badge>FastAPI</Badge><Badge>Flask</Badge><Badge>JWT</Badge><Badge>Role Guards</Badge><Badge>Decorators</Badge>
          </div>
        </Card>

        {/* softauto (NEW) */}
        <Card
          title="softauto — EDA → train → tune"
          tone="lavendar"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softauto" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softauto" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            One-call ML runs: quick EDA, robust preprocessing, feature selection, model zoo, tuning,
            plots, and an advisor that suggests fixes if accuracy lags.
          </p>
          <div className="stt-kv">
            <Badge>EDA</Badge><Badge>Feature Select</Badge><Badge>Tuning</Badge><Badge>Plots</Badge><Badge>Advisor</Badge>
          </div>
        </Card>

        {/* softdata */}
        <Card
          title="softdata — tidy data utilities"
          tone="amber"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softdata" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softdata" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            Lightweight helpers for cleaning, splitting, encoding, and reporting. Standardize preprocessing
            across notebooks and teams.
          </p>
          <div className="stt-kv">
            <Badge>Impute/Encode</Badge><Badge>Train/Test splits</Badge><Badge>Reports</Badge><Badge>Pandas</Badge>
          </div>
        </Card>

        {/* softgraph */}
        <Card
          title="softgraph — graph ML in one call"
          tone="indigo"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softgraph" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softgraph" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            Try graph learning tasks with a single helper: load graphs, pick a model, get metrics & plots.
            Great for classroom demos and quick baselines.
          </p>
          <div className="stt-kv">
            <Badge>GCN</Badge><Badge>GraphSAGE</Badge><Badge>TGAT (temporal)</Badge><Badge>Node classification</Badge>
          </div>
        </Card>

        {/* softml */}
        <Card
          title="softml — teach & try ML"
          tone="emerald"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softml" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softml" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            Minimal wrappers for quick plots and simple models. Perfect for workshops and first projects —
            <em> math first → implementation next</em>.
          </p>
          <div className="stt-kv">
            <Badge>scikit-learn</Badge><Badge>Pandas</Badge><Badge>Matplotlib</Badge>
          </div>
        </Card>
        {/* softtenant */}
        <Card
          title="softtenant — multi-tenancy, zero rewrites"
          tone="slate"
          footer={
            <div className="stt-actions">
              <Link to="/docs/softtenant" className="stt-btn stt-btn-primary">Read Docs</Link>
              <Link to="/examples/softtenant" className="stt-btn stt-btn-outline">Examples</Link>
            </div>
          }
        >
          <p className="stt-p">
            Drops multi-tenancy into existing FastAPI + SQLAlchemy apps with zero schema rewrites.
            Automatic tenant resolution via header/JWT and scoped query middleware.
          </p>
          <div className="stt-kv">
            <Badge>FastAPI</Badge><Badge>SQLAlchemy</Badge><Badge>Multi-tenancy</Badge><Badge>JWT</Badge><Badge>Middleware</Badge>
          </div>
        </Card>

      </section>
    </main>
  );
}
